import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";

import { AppShell } from "./components/layout/AppShell";
import { ProtectedRoute } from "./components/common/ProtectedRoute";
import { AuthProvider, useAuth } from "./context/AuthContext";

import { LoginPage } from "./pages/LoginPage";
import { UserManagementPage } from "./pages/UserManagementPage";
import { ResourceImportPage } from "./pages/ResourceImportPage";
import { BpmlImportPage } from "./pages/BpmlImportPage";
import { BpmlEnrichmentPage } from "./pages/BpmlEnrichmentPage";
import { TransversalSkillsPage } from "./pages/TransversalSkillsPage";
import { ApplicationScaleConfigPage } from "./pages/ApplicationScaleConfigPage";
import { ApplicationFeaturesPage } from "./pages/ApplicationFeaturesPage";
import { ReferentialsPage } from "./pages/ReferentialsPage";
import { AssessmentListPage } from "./pages/AssessmentListPage";
import { AssessmentEntryPage } from "./pages/AssessmentEntryPage";
import { MyApplicationSkillsPage } from "./pages/MyApplicationSkillsPage";
import { MyTransversalSkillsPage } from "./pages/MyTransversalSkillsPage";
import { AssessmentReviewPage } from "./pages/AssessmentReviewPage";
import { TransversalAssessmentReviewPage } from "./pages/TransversalAssessmentReviewPage";
import { ValidationDashboardPage } from "./pages/ValidationDashboardPage";
import { DashboardPage } from "./pages/DashboardPage";
import { CampaignsPage } from "./pages/CampaignsPage";
import { PreevalListPage } from "./pages/PreevalListPage";
import { PreevalEntryPage } from "./pages/PreevalEntryPage";
import { PreevalReviewPage } from "./pages/PreevalReviewPage";
import { PreevalGapsPage } from "./pages/PreevalGapsPage";
import { ExportPage } from "./pages/ExportPage";
import { NotificationConfigPage } from "./pages/NotificationConfigPage";
import { AuditLogPage } from "./pages/AuditLogPage";
import { ProfilePage } from "./pages/ProfilePage";

import { AnalyticsHubPage } from "./pages/analytics/AnalyticsHubPage";
import { HeatMapPage } from "./pages/analytics/HeatMapPage";
import { RadarPage } from "./pages/analytics/RadarPage";
import { GapAnalysisPage } from "./pages/analytics/GapAnalysisPage";
import { ProgressionPage } from "./pages/analytics/ProgressionPage";
import { TeamCoveragePage } from "./pages/analytics/TeamCoveragePage";
import { TrainingNeedsPage } from "./pages/analytics/TrainingNeedsPage";
import { DistributionPage } from "./pages/analytics/DistributionPage";
import { TransversalTeamPage } from "./pages/analytics/TransversalTeamPage";
import { DomainBenchPage } from "./pages/analytics/DomainBenchPage";
import { PlaquePage } from "./pages/analytics/PlaquePage";
import { RareExpertisePage } from "./pages/analytics/RareExpertisePage";
import { FeatureHeatmapPage } from "./pages/FeatureHeatmapPage";
import { FeatureGapPage } from "./pages/FeatureGapPage";

function DashboardHome() {
  const { user } = useAuth();
  if ((user?.accessLevel ?? 0) >= 50) {
    return <DashboardPage />;
  }
  return <Navigate to="/assessments" replace />;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />

          <Route
            element={
              <ProtectedRoute>
                <AppShell />
              </ProtectedRoute>
            }
          >
            <Route index element={<DashboardHome />} />
            <Route path="profile" element={<ProfilePage />} />

            <Route
              path="users"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <UserManagementPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="users/import"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <ResourceImportPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="bpml/import"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <BpmlImportPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="bpml/enrichment"
              element={
                <ProtectedRoute allowedRoles={["admin", "manager"]}>
                  <BpmlEnrichmentPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="transversal-skills"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <TransversalSkillsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="config/applications-scale"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <ApplicationScaleConfigPage />
                </ProtectedRoute>
              }
            />
            {/* Legacy redirect for old bookmark /config/erp-scale */}
            <Route path="config/erp-scale" element={<Navigate to="/config/applications-scale" replace />} />
            <Route
              path="config/referentials"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <ReferentialsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="config/application-features"
              element={
                <ProtectedRoute allowedRoles={["admin", "manager"]}>
                  <ApplicationFeaturesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="config/notifications"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <NotificationConfigPage />
                </ProtectedRoute>
              }
            />

            <Route path="assessments" element={<AssessmentListPage />} />
            <Route
              path="my-application-skills"
              element={
                <ProtectedRoute allowedRoles={["admin", "expert"]}>
                  <MyApplicationSkillsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="my-transversal-skills"
              element={
                <ProtectedRoute allowedRoles={["admin", "expert"]}>
                  <MyTransversalSkillsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="transversal-assessments/:id"
              element={
                <ProtectedRoute allowedRoles={["admin", "manager", "expert"]}>
                  <TransversalAssessmentReviewPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="assessments/new"
              element={
                <ProtectedRoute allowedRoles={["admin", "expert"]}>
                  <AssessmentEntryPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="assessments/:id"
              element={
                <ProtectedRoute allowedRoles={["admin", "manager", "expert"]}>
                  <AssessmentReviewPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="validation"
              element={
                <ProtectedRoute allowedRoles={["admin", "manager"]}>
                  <ValidationDashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="campaigns"
              element={
                <ProtectedRoute allowedRoles={["admin", "manager"]}>
                  <CampaignsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="preevals"
              element={
                <ProtectedRoute allowedRoles={["admin", "manager"]}>
                  <PreevalListPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="preevals/new"
              element={
                <ProtectedRoute allowedRoles={["admin", "manager"]}>
                  <PreevalEntryPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="preevals/gaps"
              element={
                <ProtectedRoute allowedRoles={["admin", "manager"]}>
                  <PreevalGapsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="preevals/:id"
              element={
                <ProtectedRoute allowedRoles={["admin", "manager"]}>
                  <PreevalReviewPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="analytics"
              element={
                <ProtectedRoute allowedRoles={["admin", "manager"]}>
                  <AnalyticsHubPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="analytics/heatmap"
              element={
                <ProtectedRoute allowedRoles={["admin", "manager"]}>
                  <HeatMapPage />
                </ProtectedRoute>
              }
            />
            <Route path="analytics/radar" element={<RadarPage />} />
            <Route
              path="analytics/gaps"
              element={
                <ProtectedRoute allowedRoles={["admin", "manager"]}>
                  <GapAnalysisPage />
                </ProtectedRoute>
              }
            />
            <Route path="analytics/progression" element={<ProgressionPage />} />
            <Route
              path="analytics/coverage"
              element={
                <ProtectedRoute allowedRoles={["admin", "manager"]}>
                  <TeamCoveragePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="analytics/training-needs"
              element={
                <ProtectedRoute allowedRoles={["admin", "manager"]}>
                  <TrainingNeedsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="analytics/distribution"
              element={
                <ProtectedRoute allowedRoles={["admin", "manager"]}>
                  <DistributionPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="analytics/transversal-team"
              element={
                <ProtectedRoute allowedRoles={["admin", "manager"]}>
                  <TransversalTeamPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="analytics/domain-bench"
              element={
                <ProtectedRoute allowedRoles={["admin", "manager"]}>
                  <DomainBenchPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="analytics/plaque"
              element={
                <ProtectedRoute allowedRoles={["admin", "manager"]}>
                  <PlaquePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="analytics/rare-expertise"
              element={
                <ProtectedRoute allowedRoles={["admin", "manager"]}>
                  <RareExpertisePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="analytics/feature-heatmap"
              element={
                <ProtectedRoute allowedRoles={["admin", "manager"]}>
                  <FeatureHeatmapPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="analytics/feature-gap"
              element={
                <ProtectedRoute allowedRoles={["admin", "manager"]}>
                  <FeatureGapPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="export"
              element={
                <ProtectedRoute allowedRoles={["admin", "manager"]}>
                  <ExportPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="audit-log"
              element={
                <ProtectedRoute allowedRoles={["admin"]}>
                  <AuditLogPage />
                </ProtectedRoute>
              }
            />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
